import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { transactionService } from '../services/transactionService';
import { profileService } from '../services/profileService';
import TransactionDetailsModal from '../components/TransactionDetailsModal';
import type { Transaction, TransactionFilters, Profile } from '../types/api';

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasScope } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [error, setError] = useState<string>('');
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profilesMap, setProfilesMap] = useState<Record<string, Profile>>({});

  const isDeveloper = hasScope('dev');
  useEffect(() => {
    loadProfiles();
  }, [isDeveloper]);

  const loadProfiles = async () => {
    try {
      const res = isDeveloper
        ? await profileService.getAllProfiles(undefined, { page: 1, limit: 1000 })
        : await profileService.getUserProfiles({ page: 1, limit: 1000 });

      if (res.success && res.data) {
        const data = Array.isArray(res.data) ? res.data : (res.data.data || []);
        const map: Record<string, Profile> = {};
        data.forEach((p: Profile) => { map[p._id] = p; });
        setProfilesMap(map);
      }
    } catch (e) {
      // Non-blocking for transactions list
      console.warn('Failed to load profiles for currency mapping:', e);
    }
  };

  const getCurrencyForTransaction = (t: Transaction): string => {
    // If profile is populated in transaction
    if (t && typeof t.profileId === 'object') {
      const p: any = t.profileId as any;
      const bank = (p?.accbank || '').toLowerCase();
      if (bank.includes('kbankpromptpay')) return 'THB';
      if (p?.currency) return String(p.currency).toUpperCase();
      return 'MYR';
    }
    // If profileId is string, try lookup from loaded profiles
    const pid = typeof t.profileId === 'string' ? t.profileId : '';
    const p = pid ? profilesMap[pid] : undefined;
    if (p) {
      const bank = (p.accbank || '').toLowerCase();
      if (bank.includes('kbankpromptpay')) return 'THB';
      return (p.currency ? p.currency.toUpperCase() : 'MYR');
    }
    return 'MYR';
  };



  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadTransactions = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = isDeveloper
        ? await transactionService.getAllTransactions(filters, { page: 1, limit: 50 })
        : await transactionService.getUserTransactions(filters, { page: 1, limit: 50 });

      if (response.success && response.data) {
        setTransactions(response.data.data || []);
      } else {
        setError(response.message || 'Failed to load transactions');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelTransaction = async (transactionId: string) => {
    if (!confirm('Are you sure you want to cancel this transaction?')) return;

    try {
      setError('');
      const response = await transactionService.cancelTransaction(transactionId, 'Cancelled by user');

      if (response.success) {
        loadTransactions(); // Reload to get updated data
      } else {
        setError(response.message || 'Failed to cancel transaction');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to cancel transaction');
    }
  };

  const handleExportTransactions = async () => {
    try {
      setError('');
      const response = await transactionService.exportTransactions(filters, 'csv');

      if (response.success && response.data) {
        // Create download link
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError(response.message || 'Failed to export transactions');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to export transactions');
    }
  };

  const handleViewDetails = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTransactionId(null);
  };

  const handleCreateTransaction = () => {
    navigate('/qr-payment');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Transactions</h1>
              <p className="text-gray-600 mt-2">
                View and manage your payment transactions
              </p>
              {isDeveloper && (
                <p className="mt-2 text-xs inline-flex items-center rounded bg-purple-100 px-2 py-1 font-medium text-purple-800">
                  Developer mode: viewing all transactions
                </p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExportTransactions}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
              >
                Export CSV
              </button>
              <button
                onClick={handleCreateTransaction}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
              >
                Create Transaction
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={filters.statusTxt || ''}
                  onChange={(e) => setFilters({...filters, statusTxt: e.target.value as any})}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={filters.transaction || ''}
                  onChange={(e) => setFilters({...filters, transaction: e.target.value as any})}
                >
                  <option value="">All Types</option>
                  <option value="transfer">Transfer</option>
                  <option value="receiving">Receiving</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={filters.dateFrom || ''}
                  onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={filters.dateTo || ''}
                  onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {transactions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg">No transactions found</div>
                <p className="text-gray-500 mt-2">Create your first transaction to get started</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <li key={transaction._id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="text-lg">
                              {transactionService.getTransactionIcon(transaction.transaction)}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">
                                {transaction.transaction === 'transfer' ? 'Transfer' : 'Receiving'}
                              </p>
                              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${transactionService.getStatusColor(transaction.statusTxt)}`}>
                                {transaction.statusTxt}
                              </span>
                              {transaction.transactiontype && (
                                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                  {transactionService.getTransactionTypeIcon(transaction.transactiontype)} {transactionService.getTransactionTypeDisplayName(transaction.transactiontype)}
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              <p>ID: {transaction._id}</p>
                              {transaction.clientId && (
                                <p className="ml-4">Client: {transaction.clientId}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {transactionService.formatAmount(transaction.amount, getCurrencyForTransaction(transaction))}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      {transaction.remark && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            <strong>Remark:</strong> {transaction.remark}
                          </p>
                        </div>
                      )}

                      {transaction.qrContent && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">
                            <strong>QR Content:</strong> {transaction.qrContent.substring(0, 50)}...
                          </p>
                        </div>
                      )}

                      <div className="mt-2 flex space-x-4">
                        <button
                          onClick={() => handleViewDetails(transaction._id)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                        {['pending', 'waiting', 'processing'].includes(transaction.statusTxt) && (
                          <button
                            onClick={() => handleCancelTransaction(transaction._id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTransactionId && (
        <TransactionDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          transactionId={selectedTransactionId}
        />
      )}
    </div>
  );
};

export default TransactionsPage;
